/**
 * LeadActionsMenu — the lead page's ONE overflow-actions surface (CRM UI
 * streamline D-6, option A).
 *
 * One component, two dresses, driven by the SAME `isPhone` value LeadHeader
 * computed from its single `useIsPhone()` call:
 *
 * - **≤640px** it IS the bottom action sheet ContactDetail has today (backdrop,
 *   drag handle, name + close X), carrying Draft options, Edit, Study info,
 *   Snooze (all four of the old SnoozeButton popover's choices — Un-snooze,
 *   done for today, +3h, and an arbitrary "Pick a time" — as inline rows
 *   rather than a second popover), Archive, Push to CTMS. Markup copied from
 *   that sheet so the phone experience is unchanged; only the z literal
 *   became a `Z` token.
 * - **≥640px** it is an anchored popover under the ⋯ button, carrying Draft
 *   options, Edit, Archive, Push to CTMS — Study info and Snooze live inline
 *   in the header instead (owner ask 2026-08-06, next to Call), so this
 *   dress does not carry a redundant copy of either.
 *
 * Two house rules are load-bearing here:
 *
 * 1. **Never unmounted.** Closed = `hidden` (display:none), never removed from
 *    the tree. `PushToCtmsButton` probes `GET /contacts/:id/ctms` on mount, so
 *    unmount/remount on every open would re-probe on every click (the D-2
 *    overlay-manager rule). Keyed by contact for the same reason ContactDetail
 *    keys it: the lead page is NOT remounted on `:id` changes, so without the
 *    key an in-flight probe/dialog would paint onto the next lead.
 * 2. **Stateful controls mount exactly once per breakpoint.** OwnerSelect and
 *    PrimaryStudySelect live in the header at ≥sm and in rows 1–2 here at <sm —
 *    never both. That is why `isPhone` is a prop, not a second `useIsPhone()`.
 *
 * The parent owns `open`, the trigger, Escape and outside-click; this component
 * only reports "close me" (backdrop, X, and every row that fires an action).
 */
import { useEffect, useState } from 'react';
import {
  Archive,
  ArchiveRestore,
  BookOpen,
  Clock,
  ListOrdered,
  Loader2,
  MoonStar,
  Pencil,
  Undo2,
  X,
} from 'lucide-react';
import { Contact, PrimaryStudyRef, contactName, studyInternalLabel } from '../../types';
import OwnerSelect from '../OwnerSelect';
import PrimaryStudySelect from '../PrimaryStudySelect';
import PushToCtmsButton from '../PushToCtmsButton';
import { isActiveSnooze, snoozeLabel } from '../SnoozeButton';
import { Z } from '../ui';

/** Action-sheet / menu row. Copied verbatim from `pages/ContactDetail.tsx`
 *  (`menuItemCls`), whose phone sheet this component replaces. */
const menuItemCls =
  'flex min-h-[44px] w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:opacity-50 dark:text-gray-200 dark:hover:bg-gray-700/60';

/** Local-time value for an `<input type="datetime-local">`. Redeclared from
 *  `components/SnoozeButton.tsx` (module-private there) so the "Pick a time"
 *  block below keeps that component's exact `min=` semantics — a snooze picker
 *  that accepts a past instant is a lead that never resurfaces. */
function toLocalInput(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export interface LeadActionsMenuProps {
  open: boolean;
  /** Fired by the backdrop, the close X, and every row that runs an action. */
  onClose: () => void;
  /** The parent's single `useIsPhone()` reading — sheet at true, popover at false. */
  isPhone: boolean;
  contact: Contact;
  primaryStudy: PrimaryStudyRef | null;
  draftingOptions: boolean;
  onDraftOptions: () => void;
  archBusy: boolean;
  onToggleArchived: () => void;
  snoozeBusy: boolean;
  onSnooze: (untilIso: string | null) => void;
  onEdit: () => void;
  onOpenStudyRef: () => void;
  onOwnerChanged: (next: string | null) => void;
  onPrimaryStudyChanged: (next: { id: string; slug: string; title: string } | null) => void;
}

export default function LeadActionsMenu({
  open,
  onClose,
  isPhone,
  contact: c,
  primaryStudy,
  draftingOptions,
  onDraftOptions,
  archBusy,
  onToggleArchived,
  snoozeBusy,
  onSnooze,
  onEdit,
  onOpenStudyRef,
  onOwnerChanged,
  onPrimaryStudyChanged,
}: LeadActionsMenuProps) {
  /** Every row closes first, then acts — today's sheet behavior exactly. */
  const run = (fn: () => void) => {
    onClose();
    fn();
  };

  // "Pick a time" — an arbitrary snooze instant, the one capability the phone
  // sheet never had and the desktop SnoozeButton popover did. Kept here (rather
  // than mounting SnoozeButton, which would open a menu inside a menu) and
  // offered in BOTH dresses, so phones gain it.
  const [pickValue, setPickValue] = useState('');
  // Never carry a half-typed time across an open/close or onto the next lead.
  useEffect(() => { setPickValue(''); }, [open, c.id]);
  const pickFuture = !!pickValue && new Date(pickValue).getTime() > Date.now();
  const firePick = () => {
    if (!pickFuture || snoozeBusy) return;
    const iso = new Date(pickValue).toISOString();
    setPickValue('');
    run(() => onSnooze(iso));
  };

  // `menuitem` only inside the real `role="menu"` popover; inside the phone
  // sheet (role="dialog") these are ordinary buttons.
  const itemRole = isPhone ? undefined : ('menuitem' as const);
  const snoozed = isActiveSnooze(c.snoozed_until);

  const rows = (
    <>
      {/* 1. Phone-only: the two selects that have no room in the compact header.
             At ≥sm they are mounted in the header instead — never both. */}
      {isPhone && (
        <>
          <label className="flex items-center gap-3 px-3 py-2">
            <span className="flex-shrink-0 text-sm font-medium text-gray-500 dark:text-gray-400">Assigned to</span>
            <OwnerSelect key={c.owner_user_id || 'unassigned'} contactId={c.id} value={c.owner_user_id} className="flex-1" onChanged={onOwnerChanged} />
          </label>
          {/* Editable at EVERY status, closed leads included (owner 2026-08-07):
              a lead who didn't qualify for one study is exactly the lead you
              want to move onto the next study's list, and hiding this behind
              "reactivate it first" made the common case the slow one. */}
          <div className="flex items-center gap-3 px-3 py-2">
            <span className="flex-shrink-0 text-sm font-medium text-gray-500 dark:text-gray-400">Considering</span>
            <PrimaryStudySelect
              contactId={c.id}
              studyId={primaryStudy?.id ?? null}
              studyTitle={primaryStudy ? studyInternalLabel(primaryStudy) : null}
              lifecycleStatus={c.lifecycle_status}
              onChanged={onPrimaryStudyChanged}
              compact
            />
          </div>
          <div className="my-1 border-t border-gray-100 dark:border-gray-700" />
        </>
      )}

      {/* 2. Draft the response options (was a visible desktop header button). */}
      <button
        type="button"
        role={itemRole}
        onClick={() => run(onDraftOptions)}
        disabled={draftingOptions || !c.phone_e164 || c.sms_opt_out}
        title={
          c.sms_opt_out
            ? 'Patient opted out of SMS'
            : !c.phone_e164
            ? 'No phone number on file'
            : 'Draft the 3 ways forward in the message box (1 book a call · 2 fill the form · 3 not interested) — you edit and send it'
        }
        className={menuItemCls}
      >
        {draftingOptions ? (
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-gray-400" />
        ) : (
          <ListOrdered className="h-4 w-4 flex-shrink-0 text-gray-400" />
        )}
        {draftingOptions ? 'Drafting…' : 'Draft the response options'}
        <span className="ml-auto flex-shrink-0 text-xs font-normal text-gray-400">1 book · 2 form · 3 no</span>
      </button>

      {/* 3. Edit contact (was the pencil icon beside the desktop name). */}
      <button
        type="button"
        role={itemRole}
        onClick={() => run(onEdit)}
        title="Edit name, phone and email"
        className={menuItemCls}
      >
        <Pencil className="h-4 w-4 flex-shrink-0 text-gray-400" />
        Edit contact
        <span className="ml-auto min-w-0 truncate text-xs font-normal text-gray-400">
          {c.email || 'name · phone · email'}
        </span>
      </button>

      {/* 4. Study reference drawer. ≥sm carries its own copy inline in the
             header now, next to Call — this row would just double it. */}
      {isPhone && (
        <button
          type="button"
          role={itemRole}
          onClick={() => run(onOpenStudyRef)}
          title="Study reference: summary, recruiter script, stipend"
          className={menuItemCls}
        >
          <BookOpen className="h-4 w-4 flex-shrink-0 text-gray-400" />
          Study info
          <span className="ml-auto flex-shrink-0 text-xs font-normal text-gray-400">script · stipend</span>
        </button>
      )}

      {/* 5. Snooze — today's sheet logic verbatim (rows, not SnoozeButton's
             popover, so a menu never opens a second menu). Phone-only, same
             reason as Study info above: ≥sm has its own SnoozeButton inline. */}
      {isPhone && (snoozed ? (
        <button
          type="button"
          role={itemRole}
          onClick={() => run(() => onSnooze(null))}
          disabled={snoozeBusy}
          className={menuItemCls}
        >
          <Undo2 className="h-4 w-4 flex-shrink-0 text-gray-400" />
          Un-snooze
          <span className="ml-auto flex-shrink-0 text-xs font-normal text-gray-400">
            until {snoozeLabel(c.snoozed_until as string)}
          </span>
        </button>
      ) : (
        <>
          <button
            type="button"
            role={itemRole}
            onClick={() =>
              run(() => {
                const d = new Date();
                d.setHours(24, 0, 0, 0); // local midnight tonight → resurfaces tomorrow
                onSnooze(d.toISOString());
              })
            }
            disabled={snoozeBusy}
            className={menuItemCls}
          >
            <MoonStar className="h-4 w-4 flex-shrink-0 text-primary-600 dark:text-primary-400" />
            Snooze — done for today
          </button>
          <button
            type="button"
            role={itemRole}
            onClick={() => run(() => onSnooze(new Date(Date.now() + 3 * 3600e3).toISOString()))}
            disabled={snoozeBusy}
            className={menuItemCls}
          >
            <Clock className="h-4 w-4 flex-shrink-0 text-gray-400" />
            Snooze — later today (+3h)
          </button>
        </>
      ))}

      {/* 5c. …and an arbitrary instant. Mirrors SnoozeButton's picker block
             (label + datetime-local floored at now + a disabled-until-future
             confirm), adapted to the menu width. Closes the menu on fire.
             Phone-only — ≥sm's inline SnoozeButton carries its own picker. */}
      {isPhone && (
        <div role="none" className="mt-1 border-t border-gray-100 px-3 pb-1 pt-2 dark:border-gray-700">
          <label
            htmlFor={`lead-snooze-until-${c.id}`}
            className="block text-[11px] font-medium text-gray-500 dark:text-gray-400"
          >
            Pick a time
          </label>
          <input
            id={`lead-snooze-until-${c.id}`}
            type="datetime-local"
            value={pickValue}
            min={toLocalInput(new Date())}
            disabled={snoozeBusy}
            onChange={(e) => setPickValue(e.target.value)}
            className="mt-1 min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-900 transition-colors duration-150 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-wait disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 sm:min-h-0"
          />
          <button
            type="button"
            onClick={firePick}
            disabled={!pickFuture || snoozeBusy}
            className="mt-1.5 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-lg bg-primary-600 px-2 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
          >
            <MoonStar className="h-3.5 w-3.5" /> Snooze until then
          </button>
        </div>
      )}

      {/* 6. Archive / unarchive (was a visible desktop header button). */}
      <button
        type="button"
        role={itemRole}
        onClick={() => run(onToggleArchived)}
        disabled={archBusy}
        title={c.archived_at ? 'Unarchive this conversation' : 'Archive this conversation'}
        className={menuItemCls}
      >
        {archBusy ? (
          <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-gray-400" />
        ) : c.archived_at ? (
          <ArchiveRestore className="h-4 w-4 flex-shrink-0 text-gray-400" />
        ) : (
          <Archive className="h-4 w-4 flex-shrink-0 text-gray-400" />
        )}
        {c.archived_at ? 'Unarchive conversation' : 'Archive conversation'}
      </button>

      {/* 7. CTMS push, mounted wholesale: it owns its own chip, dialog
             (Z.modalRaised, i.e. above this surface) and probe. Its internal
             clicks deliberately do NOT close the menu — same as today's sheet. */}
      <div role="none" className="px-3 py-2">
        <PushToCtmsButton key={c.id} contactId={c.id} />
      </div>
    </>
  );

  if (isPhone) {
    return (
      <div
        className={`fixed inset-0 ${Z.modal} ${open ? '' : 'hidden'}`}
        role="dialog"
        aria-modal="true"
        aria-label="More lead actions"
      >
        <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 max-h-[80vh] overflow-y-auto rounded-t-2xl bg-white p-3 pb-[calc(1rem+env(safe-area-inset-bottom))] text-left shadow-xl dark:bg-gray-800">
          <div className="mx-auto mb-2 h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-600" aria-hidden="true" />
          <div className="mb-1 flex items-center justify-between gap-2 px-1">
            <h3 className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">
              {contactName(c)}
            </h3>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close actions"
              className="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-lg text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {rows}
        </div>
      </div>
    );
  }

  return (
    <div
      role="menu"
      aria-label="More lead actions"
      className={`absolute right-0 top-full mt-1.5 w-72 ${Z.overlay} rounded-xl border border-gray-200 bg-white p-1.5 text-left shadow-lg dark:border-gray-600 dark:bg-gray-900 ${
        open ? '' : 'hidden'
      }`}
    >
      {rows}
    </div>
  );
}
